import humps from 'humps';
import type { Context } from '@nbx/frontend-helpers/auth';
import { parseResponseBody } from './response';

enum TokenClaimsEnum {
  jti = 'jti',
  act = 'act',
  sub = 'sub',
  roles = 'roles',
  iss = 'iss',
  aud = 'aud',
  iat = 'iat',
  exp = 'exp',
  session = 'session'
}

type ParsedJWT = {
  [key in TokenClaimsEnum]?: string;
};

const parseJwt = (token?: string): ParsedJWT | null => {
  if (!token) return null;
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    console.warn('cannot parse messsage', e);
    return null;
  }
};

interface APIFetchErrorProps {
  message: string;
  request: FetchRequest;
  response: Response;
  originalError?: Record<string, unknown> | null;
  contextId?: string;
  contextToken?: string;
}

const ID_SEGMENT =
  /^(?:[0-9]+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-zA-Z_-]{20,})$/;

/**
 * Endpoint shape without the identifiers, so every call to one endpoint reports as one error
 * rather than one per request. Error Tracking groups on the message.
 */
export const normalizeUrlPath = (url: string): string => {
  let path = url;
  try {
    path = new URL(url, window.location.origin).pathname;
  } catch {
    path = url.split('?')[0];
  }
  return (
    path
      .split('/')
      .map(segment => (ID_SEGMENT.test(segment) ? ':id' : segment))
      .join('/') || '/'
  );
};

export class APIFetchError extends Error {
  response: Response;

  originalError?: Record<string, unknown> | null;

  url: string;

  /**
   * Full request/response/token detail, kept off `message` so the message stays groupable. The name is
   * load-bearing: `dd_context` is the only own property the Datadog SDK reads off an Error, so under
   * any other name this payload never leaves the browser.
   */
  dd_context: Record<string, unknown>;

  constructor({ message, request, response, originalError, contextId, contextToken }: APIFetchErrorProps) {
    super(
      message ??
        `Api call failed: ${response?.status} ${request.method.toUpperCase()} ${normalizeUrlPath(request.url)}`
    );
    // Without this every APIFetchError reports as kind `Error`, alongside every other error.
    this.name = 'APIFetchError';
    this.response = response;
    this.url = request.url;
    this.originalError = originalError;

    const { jti, act, sub, roles, iss, aud, iat, exp, session } = parseJwt(contextToken) ?? {};
    this.dd_context = {
      request: {
        ...request,
        body: request.body?.slice(0, 100),
        headers: { ...request.headers, Authorization: '/*removed*/' }
      },
      response: { status: response?.status, statusText: response?.statusText },
      originalError,
      contextId,
      contextToken: {
        jti,
        act,
        sub,
        roles,
        iss,
        aud,
        iat: iat && new Date(+iat * 1000).toISOString(),
        exp: exp && new Date(+exp * 1000).toISOString(),
        session
      }
    };
  }
}

export interface FetchRequest {
  url: string;
  method: string;
  body?: string;
  headers?: Record<string, string>;
}

export interface NextPageInfo {
  nextPageUrl: string;
  pagingState: string;
}

export interface RequestOptions {
  decamelizeRequestKeys?: boolean;
  camelizeResponseKeys?: boolean;
  returnHeaders?: boolean;
  accept?: string;
}

export interface Payload {
  headers?: Record<string, string>;
  json?: Record<string, unknown>;
}

export interface PagedResponse {
  body: string | Record<string, unknown>;
  nextPageUrl?: string;
  pagingState?: string;
}

export const createRequest = (
  method: string,
  url: string,
  payload: Payload = {},
  accept = 'application/json',
  options?: RequestOptions
): FetchRequest => {
  const { headers = {}, json = null } = payload;
  const decamelizeRequestKeys = options?.decamelizeRequestKeys ?? true;

  const request = {
    url,
    method,
    body: json ? JSON.stringify(decamelizeRequestKeys ? humps.decamelizeKeys(json) : json) : undefined,
    headers: {
      ...headers,
      Accept: accept
    }
  };

  if (json) request.headers['Content-Type'] = 'application/json';
  return request;
};

export const sendRequest = async (
  request: FetchRequest,
  context?: Context,
  accept = 'application/json',
  options?: RequestOptions
): Promise<string | Record<string, unknown>> => {
  const response = await doFetch(request, context);
  const handled = await handleStatus(response, request, context);
  return parseResponseBody(handled, accept, options);
};

export const sendRequestExternal = async (
  request: FetchRequest,
  accept = 'application/json',
  options?: RequestOptions
): Promise<string | Record<string, unknown>> => {
  const response = await doFetchExternal(request);
  const handled = await handleStatus(response, request, undefined, true);
  return parseResponseBody(handled, accept, options);
};

export const sendPagedRequest = async (
  request: FetchRequest,
  context?: Context,
  accept = 'application/json',
  options?: RequestOptions
): Promise<PagedResponse> => {
  const response = await doFetch(request, context);
  const handled = await handleStatus(response, request, context);
  const parsedBody = await parseResponseBody(handled, accept, options);
  const nextPageInfo = await handlePagingState(handled);
  if (nextPageInfo !== null) {
    const { pagingState, nextPageUrl } = nextPageInfo;
    let envFormattedNextPageUrl: string | undefined;
    if (nextPageUrl) {
      envFormattedNextPageUrl = nextPageUrl;
      if (window.location.origin.includes('localhost') && window.PLATFORM === 'web') {
        envFormattedNextPageUrl = envFormattedNextPageUrl.replace(
          'https://api.nbx.com',
          `${window.location.origin}/api`
        );
        envFormattedNextPageUrl = envFormattedNextPageUrl.replace(
          'https://api-preview.nbx.com',
          `${window.location.origin}/api`
        );
      }
    }

    return {
      body: parsedBody,
      pagingState,
      nextPageUrl: envFormattedNextPageUrl
    };
  }
  return { body: parsedBody };
};

const parseErrorBody = async (response: Response): Promise<Record<string, unknown> | null> => {
  const contentType =
    typeof response?.headers?.get === 'function' ? response.headers.get('content-type') : null;
  if (!contentType?.includes('application/json')) return null;
  try {
    // Read a clone, so `APIFetchError.response` still has an unconsumed body for callers.
    return await (typeof response.clone === 'function' ? response.clone() : response).json();
  } catch (e) {
    console.warn('cannot parse response.json() from an API', e);
    return null;
  }
};

export const handleStatus = async (
  response: Response,
  request: FetchRequest,
  context?: Context,
  external = false,
  retry = false
): Promise<Response> => {
  if (response.ok) {
    return response;
  }

  const originalError = await parseErrorBody(response);

  if (!retry && (response.status === 401 || response.status === 403)) {
    if (external) {
      return handleStatus(
        await doFetchExternal(request),
        request,
        undefined,
        true, // this is external
        true // is a retry
      );
    }
    if (context && typeof context.refresh === 'function') {
      const refreshedContext = await context.refresh();
      console.log('retrying request with refreshed context...');
      return handleStatus(
        await doFetch(request, refreshedContext),
        request,
        refreshedContext,
        external,
        true // is a retry
      );
    }
  }

  if (response.status === 401 && window.PLATFORM === 'web') {
    window.LOGOUT();
  }
  // message, response, originalError, url, method, body, headers
  throw new APIFetchError({
    message: `Api call failed: ${response.status} ${request.method.toUpperCase()} ${normalizeUrlPath(request.url)}`,
    request,
    response,
    originalError,
    contextId: context?.id,
    contextToken: context?.token
  });
};

const handlePagingState = async (response): Promise<NextPageInfo | null> => {
  if (!response.headers) return null;
  return {
    pagingState: response.headers.get('x-paging-state'),
    nextPageUrl: response.headers.get('x-next-page-url')
  };
};

export const doFetch = async (request: FetchRequest, context?: Context): Promise<Response> => {
  const { method, url, headers = {}, body } = request;
  const token = context ? context.token : null;
  if (token !== null && token !== undefined) {
    const bearer = `bearer ${token}`;
    headers.Authorization = bearer;
  }
  return fetch(url, {
    method,
    body,
    credentials: 'include',
    headers
  });
};

export const doFetchExternal = async (request: FetchRequest): Promise<Response> => {
  const { method, url, headers, body } = request;
  return fetch(url, {
    method,
    body,
    headers
  });
};
